<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';

	export const config: SectionConfig = {
		collection: 'section_contact',
		availableFor: ['pages'],
		query: gql`
        internal_name
        translations {
            languages_code {
                code
            }
            title
            text
        }
        items {
            section_contact_item_id {
                email
                phone
                media {
                    ...Media
                }
				translations {
					languages_code {
						code
					}
					name
					position
					address
					opening_hours
				}

            }
        }


    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		text: string;
		items: any;
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import Text from '$lib/components/Text.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
</script>

<Section gapType="padding">
	<div class="grid">
		<div class="text-wrapper">
			<div class="title">{data.title}</div>
			<div class="divider" />
			<div class="text"><Wysiwyg text={data.text} /></div>
		</div>
		<div class="wrapper">
			{#each data.items as { section_contact_item_id: item }, idx}
				<div class="container">
					<div class="media"><Media {...item.media} transform="l" fit="cover" /></div>
					<div>
						<div class="content">
							<div>
								{#if item.name}
									<div class="name">{item.name}</div>
								{/if}
								{#if item.position}
									<div class="position">{item.position}</div>
								{/if}
							</div>
							{#if item.address}
								<div class="address"><span>A</span><Wysiwyg text={item.address} /></div>
							{/if}
							<div>
								{#if item.phone}
									<div class="phone">
										<span>P</span><a href={'tel:' + item.phone}>{item.phone}</a>
									</div>
								{/if}
								{#if item.email}
									<div class="email">
										<span>M</span><a href={'mailto:' + item.email}>{item.email}</a>
									</div>
								{/if}
							</div>
							{#if item.opening_hours}
								<div class="opening-hours">
									<div class="opening-hours-title">
										<Text key="opening_hours" />
									</div>
									<Wysiwyg text={item.opening_hours} />
								</div>
							{/if}
						</div>
					</div>
				</div>
			{/each}
		</div>
	</div>
</Section>

<style lang="scss">
	.grid {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-xl);
	}
	.title {
		@include font(h1);
		padding-bottom: v(content-gap);
	}
	.divider {
		width: 2.8rem;
		height: 3px;
		background: color(black);
		margin-top: -0.1rem;
	}
	.text {
		padding-top: v(content-gap-sm);
		@include font(text-lg);
		max-width: 25ch;
	}
	.name {
		@include font(text-lg);
	}
	.position {
		@include font(text-sm);
		font-weight: bold;
	}
	.wrapper {
		gap: v(content-gap-lg);
		display: grid;
		gap: v(content-gap-2xl);
		grid-template-columns: repeat(3, minmax(0, 1fr));
		@media (max-width: 900px) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
		}

		@include media(phone) {
			padding-top: 0;
		}
		@include media(phone) {
			flex-direction: column;
		}
	}
	.container {
		display: flex;
		flex-direction: column;
		background: color(white);
		border-radius: v(border-radius);
		gap: v(content-gap-2xs);
		position: relative;

		:global(a) {
			color: color(black);
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}
		span {
			padding-right: v(content-gap);
			@include font(text);
			font-weight: bold;
		}
	}
	.address {
		display: flex;
	}
	.content {
		padding: v(content-padding);
		gap: v(content-gap-sm);
		display: flex;
		flex-direction: column;
	}
	.media {
		position: relative;
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: flex-end;
		:global(img) {
			width: 60%;
			height: auto;
			@media (max-width: 900px) {
				aspect-ratio: 1/1;
			}
		}
	}
	.opening-hours-title {
		@include font(text);
		font-weight: bold;
	}
</style>
